import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

const IMG_BASE = "/assets/project/First Bangla Voice Command Drone";

const Figure = ({ src, alt, caption, isVideo = false }) => (
  <figure className={styles.figureCard}>
    {isVideo ? (
      <video
        src={src}
        controls
        className={styles.videoPlayer}
      />
    ) : (
      <img src={src} alt={alt} className={styles.galleryImage} />
    )}
    <figcaption className={styles.galleryCaption}>
      {caption}
    </figcaption>
  </figure>
);

export default function VoiceDronePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          A Voice-Operated Remotely Piloted Aircraft Capable of Receiving Voice Commands in Bangla
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee (Team Leader)</span> &nbsp;•&nbsp; Mar 2023 – May 2024
        </div>
        <div className={styles.affiliations}>
          Aviation and Aerospace University, Bangladesh (AAUB) — Supervised by Ishtiaq Ibn Salam (Lecturer, Dept. of Aerospace Engineering)
        </div>
        <div className={styles.badge} style={{ background: 'rgba(37, 99, 235, 0.1)', color: '#2563eb', border: '1px solid rgba(37, 99, 235, 0.3)' }}>
          💡 Patent No. 1007044 (Issued March 25, 2026)
        </div>
        <div className={styles.headerActions}>
          <a
            href="https://drive.google.com/file/d/11mwIt6Rfgh7tgO4DQjoFsS4saKwHdQvY/view?usp=sharing"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#2563eb',
              textDecoration: 'none',
              background: 'rgba(37, 99, 235, 0.06)',
              padding: '0.5rem 1.05rem',
              borderRadius: '8px',
              border: '1px solid rgba(37, 99, 235, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            📄 View Patent Certificate →
          </a>
          <a
            href="https://www.linkedin.com/posts/saf123_our-bangla-voice-command-drone-acharjo-share-7223977058220044290-FfQ3/"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#0077b5',
              textDecoration: 'none',
              background: 'rgba(0, 119, 181, 0.06)',
              padding: '0.5rem 1.05rem',
              borderRadius: '8px',
              border: '1px solid rgba(0, 119, 181, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            🔗 View LinkedIn Project →
          </a>
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Project Overview</h3>
        <p className={styles.abstractText}>
          As unmanned aerial vehicles (UAVs) become critical for emergency response, search-and-rescue, and surveillance in Bangladesh, operational accessibility remains a bottleneck. Traditional RC controllers require extensive training. To solve this, I led a 10-person engineering team to develop a Bangla voice-command interface for UAV operations, integrating Google Cloud Speech-to-Text with MAVLink through MAVProxy, IoT connectivity, and a Smart Virtual Assistant. Evaluated through more than 50 outdoor flight tests under varying acoustic and environmental conditions, the system achieved 95% command recognition accuracy and resulted in Patent No. 1007044, issued on March 25, 2026.
        </p>
        <div className={styles.keywords}>
          <strong>Technologies:</strong> UAV Design, Natural Language Processing (NLP), IoT, ArduPilot, MAVLink, MAVProxy, Google Cloud Platform, Embedded Systems.
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>System Architecture & Integration</h2>
        <p className={styles.paragraph}>
          The core engineering challenge was establishing a low-latency, highly reliable pipeline between human speech and mechanical actuation. The architecture utilizes a ground-station microphone to capture the operator's Bangla commands. This audio telemetry is streamed via an IoT network to the Google Cloud Speech-to-Text API for processing. A custom Python script parses the generated text, cross-references it with a dictionary of authorized flight commands (e.g., Takeoff, Land, Yaw, Pitch), and transmits the corresponding MAVLink protocol signals directly to the onboard ArduPilot flight controller via MAVProxy.
        </p>
        <p className={styles.paragraph}>
          Redundancy protocols were programmed to initiate automatic hover or return-to-launch (RTL) states if network connectivity drops or a command is misunderstood. The hardware assembly involved custom wiring, flight controller configuration, and extensive bench testing before field deployment.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/druing work.jpeg`}
            alt="Development Work"
            caption="Figure 1: Ground station setup with laptop running MAVProxy and the voice command parsing script during indoor integration testing."
          />
          <Figure
            src={`${IMG_BASE}/druing work 2.jpeg`}
            alt="Development Work 2"
            caption="Figure 2: Custom-built quadcopter on the workbench with exposed flight controller, telemetry radio, and power distribution board."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Flight Testing & Performance</h2>
        <p className={styles.paragraph}>
          Hardware design and software integration are only successful if validated in the field. The UAV underwent rigorous flight testing protocols at the Aviation and Aerospace University, Bangladesh campus and surrounding agricultural fields. Over 50+ autonomous outdoor flight tests were executed under varying acoustic and environmental conditions, achieving 95% command recognition accuracy.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>95%</div>
            <div className={styles.metricLabel}>Recognition Accuracy</div>
            <p className={styles.metricSubtext}>
              Validated under varying outdoor acoustic conditions.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>50+</div>
            <div className={styles.metricLabel}>Outdoor Flight Tests</div>
            <p className={styles.metricSubtext}>
              Successfully executed with Bangla voice commands.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>10</div>
            <div className={styles.metricLabel}>Engineers Led</div>
            <p className={styles.metricSubtext}>
              Managed cross-functional hardware and software teams.
            </p>
          </div>
        </div>

        <p className={styles.paragraph}>
          The video below demonstrates a night-time tethered flight test where the drone maintains stable hover while responding to voice commands. Field tests were conducted in both day and night conditions to validate all-weather operational reliability.
        </p>

        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/flight test.mp4`}
            alt="Night Flight Test"
            caption="Figure 3: Night-time tethered flight test demonstrating stable hover."
            isVideo={true}
          />
          <Figure
            src={`${IMG_BASE}/voice_ac.mp4`}
            alt="Night Flight Test 2"
            caption="Figure 4: Flight test via Bangla voice command. The operator issues commands from the ground station, and the drone responds in real-time."
            isVideo={true}
          />
        </div>

        <div className={styles.galleryGrid} style={{ marginTop: '1.5rem' }}>
          <Figure
            src={`${IMG_BASE}/pre_fight_check.jpg`}
            alt="Pre-Flight Check"
            caption="Figure 5: Pre-flight inspection and propeller balance check before a test mission."
          />
          <Figure
            src={`${IMG_BASE}/field work.jpeg`}
            alt="Field Work"
            caption="Figure 6: Daytime field testing over agricultural terrain with the team monitoring telemetry."
          />
          <Figure
            src={`${IMG_BASE}/field work 2.jpeg`}
            alt="Field Work Radio"
            caption="Figure 7: RadioLink AT10 transmitter used as backup manual override during autonomous voice-command flights."
          />
          <Figure
            src={`${IMG_BASE}/iconic photo.jpeg`}
            alt="Team with Drone"
            caption="Figure 8: After a successful flight test."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Aerial Telemetry & Imaging</h2>
        <p className={styles.paragraph}>
          During testing, the onboard camera captured high-resolution aerial telemetry. The images below show aerial photographs taken directly from the UAV during autonomous voice-controlled flight tests over the campus and surrounding terrain, demonstrating the system's practical utility for surveying and reconnaissance.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/take_photo_using_our_drone.png`}
            alt="Aerial Terrain View"
            caption="Figure 9: Aerial survey image captured during autonomous flight — rural settlement and agricultural grid mapping."
          />
          <Figure
            src={`${IMG_BASE}/take_photo_using_our_drone (2).png`}
            alt="Aerial Campus View"
            caption="Figure 10: Top-down aerial view of the AAUB campus and airfield captured by the onboard camera."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Patent Recognition</h2>
        <p className={styles.paragraph}>
          The success of this project proves that advanced aerospace technologies can be localized to overcome language barriers in developing nations. Because of the novel integration of Bangla voice control, IoT connectivity, and a virtual assistant for emergency-response UAV applications, the system was awarded <strong>Patent No. 1007044</strong> by the Government of Bangladesh, Department of Patents, Industrial Designs and Trademarks, issued on March 25, 2026.
        </p>
        <div style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '1.5rem 1.75rem',
          marginTop: '1.5rem',
          boxShadow: 'var(--shadow-xs)'
        }}>
          <h4 style={{ margin: '0 0 0.65rem 0', color: 'var(--fg)', fontSize: '1.05rem' }}>Patent Details</h4>
          <p style={{ margin: '0 0 0.35rem 0', fontSize: '0.92rem', color: 'var(--text-secondary)' }}>
            <strong>Title:</strong> A Voice-Operated Remotely Piloted Aircraft Capable of Receiving Voice Commands in Bangla
          </p>
          <p style={{ margin: '0 0 0.35rem 0', fontSize: '0.92rem', color: 'var(--text-secondary)' }}>
            <strong>Patent Number:</strong> 1007044
          </p>
          <p style={{ margin: '0 0 0.35rem 0', fontSize: '0.92rem', color: 'var(--text-secondary)' }}>
            <strong>Application:</strong> BD-P-2024-101
          </p>
          <p style={{ margin: '0 0 1.1rem 0', fontSize: '0.92rem', color: 'var(--text-secondary)' }}>
            <strong>Issued:</strong> March 25, 2026
          </p>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
            <a
              href="https://drive.google.com/file/d/11mwIt6Rfgh7tgO4DQjoFsS4saKwHdQvY/view?usp=sharing"
              target="_blank"
              rel="noreferrer"
              style={{
                fontSize: '0.86rem',
                fontWeight: 700,
                color: '#fff',
                textDecoration: 'none',
                background: '#2563eb',
                padding: '0.55rem 1.2rem',
                borderRadius: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem'
              }}
            >
              📄 View Patent Certificate →
            </a>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Team & Collaboration</h2>
        <p className={styles.paragraph}>
          This project was made possible through the collaboration of a dedicated 10-person interdisciplinary team spanning aerospace engineering, computer science, and electronics. From late-night debugging sessions to early-morning flight tests, the team's commitment to building accessible aerospace technology for Bangladesh was unwavering.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/team_members.png`}
            alt="Team Members"
            caption="Figure 11: The core engineering team in uniform at the AAUB campus with the voice-command drone."
          />
          <Figure
            src={`${IMG_BASE}/team_meeting.jpeg`}
            alt="Team Meeting"
            caption="Figure 12: Team celebration dinner after achieving successful voice-command flight validation."
          />
          <Figure
            src={`${IMG_BASE}/team_meeting 2.jpeg`}
            alt="Team Meeting 2"
            caption="Figure 13: Post-flight debrief and planning session over lunch."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          The Bangla Voice-Operated UAV represents a significant step toward democratizing drone technology in Bangladesh. By eliminating the need for complex RC controller training and enabling natural-language interaction in Bangla, this system opens emergency-response and surveillance capabilities to a broader population. The granted patent (Patent No. 1007044, issued March 25, 2026) validates the novelty of the approach, while 50+ successful outdoor flight tests with 95% recognition accuracy confirm its real-world viability.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.75rem', flexWrap: 'wrap', gap: '1rem' }}>
          <a
            href="https://www.linkedin.com/posts/saf123_our-bangla-voice-command-drone-acharjo-share-7223977058220044290-FfQ3/"
            target="_blank"
            rel="noreferrer"
            style={{
              background: '#0077b5',
              color: '#fff',
              border: 'none',
              padding: '0.7rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.92rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(0, 119, 181, 0.25)'
            }}
          >
            🔗 View Full Project on LinkedIn
          </a>
          <a
            href="https://drive.google.com/file/d/11mwIt6Rfgh7tgO4DQjoFsS4saKwHdQvY/view?usp=sharing"
            target="_blank"
            rel="noreferrer"
            style={{
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              padding: '0.7rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '0.92rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)'
            }}
          >
            📄 View Patent Certificate
          </a>
        </div>
      </section>

    </div>
  );
}